import { ReactNode, useMemo } from 'react';
import { isBefore } from 'date-fns';
import { AlertTriangle, CalendarClock, ChevronRight, Inbox, Scale, ShieldAlert, Sparkles } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/hooks/useUserRole';
import { hasFeatureAccess } from '@/lib/permissions';
import { useTodayData, type TodayRisk } from '@/hooks/useTodayData';
import { useLocalization } from '@/hooks/useLocalization';

interface TodayViewProps {
  onNavigate: (view: string) => void;
}

const RULE_LABELS: Record<string, string> = {
  release_health_degraded: 'Release health degraded',
  blocked_dependency: 'Blocked dependency',
  overdue_not_done: 'Overdue and not done',
};
const ruleLabel = (id: string) => RULE_LABELS[id] ?? id.replace(/_/g, ' ');

// Status uses colour + icon + text together (design guide §3.9: colour never carries meaning alone).
const SEVERITY_STYLE: Record<string, string> = {
  critical: 'bg-[hsl(var(--masira-priority-critical))]/10 text-[hsl(var(--masira-priority-critical))]',
  high: 'bg-destructive/10 text-destructive',
  medium: 'bg-warning/15 text-foreground',
  low: 'bg-muted text-muted-foreground',
};
const SeverityPill = ({ severity }: { severity: string | null }) => {
  const { t } = useLocalization();
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide', SEVERITY_STYLE[severity ?? 'low'] ?? SEVERITY_STYLE.low)}>
      {t(severity ?? 'info')}
    </span>
  );
};

const Block = ({ icon, title, action, onAction, children }: { icon: ReactNode; title: string; action?: string; onAction?: () => void; children: ReactNode }) => {
  const { t } = useLocalization();
  return (
  <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-primary" aria-hidden="true">{icon}</span>
      <h2 className="font-heading text-lg font-semibold text-foreground">{t(title)}</h2>
      {action && onAction && (
        <button type="button" onClick={onAction} className="ms-auto inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
          {t(action)} <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
        </button>
      )}
    </div>
    {children}
  </section>
  );
};

const Empty = ({ children }: { children: ReactNode }) => <p className="py-2 text-sm text-muted-foreground">{children}</p>;

const Row = ({ onClick, children }: { onClick?: () => void; children: ReactNode }) => (
  <li>
    <button type="button" onClick={onClick} className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-start hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {children}
    </button>
  </li>
);

const TodayView = ({ onNavigate }: TodayViewProps) => {
  const { tx, t, ago, longDate, shortDate } = useLocalization();
  const { role } = useUserRole();
  const d = useTodayData();
  const canComms = hasFeatureAccess(role, 'inbox') || hasFeatureAccess(role, 'meetings');
  const canApprovals = hasFeatureAccess(role, 'hitlWorkbench');
  const canDecisions = hasFeatureAccess(role, 'decisions');

  const nameFor = useMemo(() => {
    const byId = new Map<string, string>();
    d.projects.forEach((p) => { byId.set(p.id, p.name); if (p.external_id) byId.set(p.external_id, p.name); });
    d.tasks.forEach((t) => { if (t.external_id) byId.set(t.external_id, t.title); });
    return (r: TodayRisk) => byId.get(r.entity_id) ?? `${r.entity_type} ${r.entity_id.slice(0, 8)}`;
  }, [d.projects, d.tasks]);

  const now = new Date();
  const overdue = d.tasks.filter((t) => t.due_date && isBefore(new Date(t.due_date), now));
  const blocked = d.tasks.filter((t) => t.status === 'blocked');
  const attention = [...blocked, ...overdue.filter((t) => t.status !== 'blocked')].slice(0, 4);
  const health = { onTrack: 0, atRisk: 0, delayed: 0 };
  d.projects.forEach((p) => {
    if (p.health === 'on-track') health.onTrack++;
    else if (p.health === 'delayed') health.delayed++;
    else if (p.health) health.atRisk++;
  });
  const highRisks = d.risks.filter((r) => r.severity === 'high' || r.severity === 'critical');
  const newRisks = d.risks.filter((r) => now.getTime() - new Date(r.detected_at).getTime() < 24 * 3600_000);
  const noData = !d.isLoading && d.projects.length === 0 && d.emails.length === 0 && d.risks.length === 0 && d.approvals.length === 0;

  // Rule-based briefing from the numbers on this screen; no AI call, nothing invented.
  const briefing: string[] = [];
  if (d.projects.length) {
    briefing.push(t(d.projects.length === 1 ? '{n} project: {onTrack} on track, {atRisk} at risk, {delayed} delayed.' : '{n} projects: {onTrack} on track, {atRisk} at risk, {delayed} delayed.', { n: d.projects.length, ...health }));
  }
  if (d.risks.length) {
    briefing.push(`${t(d.risks.length === 1 ? '{n} open risk ({high} high or critical)' : '{n} open risks ({high} high or critical)', { n: d.risks.length, high: highRisks.length })}${newRisks.length ? t(', {new} new in the last 24 hours', { new: newRisks.length }) : ''}.`);
  }
  const waiting = [
    canApprovals && d.approvals.length ? t(d.approvals.length === 1 ? '{n} AI output awaiting review' : '{n} AI outputs awaiting review', { n: d.approvals.length }) : '',
    canDecisions && d.decisions.length ? t(d.decisions.length === 1 ? '{n} decision pending' : '{n} decisions pending', { n: d.decisions.length }) : '',
    blocked.length ? t(blocked.length === 1 ? '{n} blocked task' : '{n} blocked tasks', { n: blocked.length }) : '',
  ].filter(Boolean);
  if (waiting.length) briefing.push(t('Waiting on you or your team: {items}.', { items: waiting.join(t(', ')) }));
  if (canComms && d.unreadCount) briefing.push(t(d.unreadCount === 1 ? '{n} unread message.' : '{n} unread messages.', { n: d.unreadCount }));

  if (d.isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label={t("Loading today's summary")}>
        <Skeleton className="h-28 w-full" />
        <div className="grid gap-4 lg:grid-cols-2"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-border bg-card p-5 shadow-sm" aria-labelledby="briefing-title">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
          <h2 id="briefing-title" className="font-heading text-lg font-semibold">{t('Your briefing for {date}', { date: longDate(now) })}</h2>
          <span className="ms-auto font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('from your connected data')}</span>
        </div>
        {noData ? (
          <p className="text-sm text-muted-foreground">
            {t('Nothing to brief yet. Connect a source (Jira, Microsoft 365, Azure DevOps) and Masira will summarise communication, risks and decisions here every day.')}
          </p>
        ) : (
          <ul className="space-y-1.5 text-[15px] leading-relaxed">
            {briefing.map((l) => <li key={l} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />{l}</li>)}
          </ul>
        )}
        {d.failed > 0 && (
          <p role="status" className="mt-3 text-xs text-muted-foreground">{t('Some sections could not be loaded; the numbers above may be incomplete.')}</p>
        )}
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        {canComms && (
          <Block icon={<Inbox className="h-5 w-5" />} title={t('Communication')} action={t('Open inbox')} onAction={() => onNavigate('inbox')}>
            {d.emails.length === 0 && d.meetings.length === 0 ? (
              <Empty>{t('No unread messages or recent meetings.')}</Empty>
            ) : (
              <>
                <ul>
                  {d.emails.map((e) => (
                    <Row key={e.id} onClick={() => onNavigate('inbox')}>
                      {e.escalation_flag ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-label={t('Escalated')} /> : <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{e.subject ?? t('(no subject)')}</span>
                        <span className="block truncate text-xs text-muted-foreground">{e.sender_name} · {ago(e.received_at)}</span>
                      </span>
                    </Row>
                  ))}
                </ul>
                {d.meetings.length > 0 && (
                  <>
                    <h3 className="mt-3 mb-1 px-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{t('Recent meetings')}</h3>
                    <ul>
                      {d.meetings.map((m) => (
                        <Row key={m.id} onClick={() => onNavigate('meetings')}>
                          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold">{tx(m.title)}</span>
                            <span className="block text-xs text-muted-foreground">
                              {ago(m.start_time)}{m.ai_action_items?.length ? ` · ${t(m.ai_action_items.length === 1 ? '{n} action item' : '{n} action items', { n: m.ai_action_items.length })}` : ''}
                            </span>
                          </span>
                        </Row>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </Block>
        )}

        <Block icon={<ShieldAlert className="h-5 w-5" />} title={t('Risks')} action={t('All risks')} onAction={() => onNavigate('risks')}>
          {d.risks.length === 0 ? (
            <Empty>{t('No open risks. Risk signals appear here when a rule detects a problem.')}</Empty>
          ) : (
            <ul>
              {d.risks.slice(0, 6).map((r) => (
                <Row key={r.id} onClick={() => onNavigate('risks')}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{nameFor(r)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{tx(ruleLabel(r.rule_id))} · {t('detected {ago}', { ago: ago(r.detected_at) })}</span>
                  </span>
                  <SeverityPill severity={r.severity} />
                </Row>
              ))}
            </ul>
          )}
        </Block>

        {(canApprovals || canDecisions) && (
          <Block icon={<Scale className="h-5 w-5" />} title={t('Needs your attention')} action={t('Decide')} onAction={() => onNavigate(canDecisions ? 'decideBoard' : 'hitlWorkbench')}>
            {(canApprovals ? d.approvals.length : 0) + (canDecisions ? d.decisions.length : 0) + attention.length === 0 ? (
              <Empty>{t('Nothing is waiting on you.')}</Empty>
            ) : (
              <ul>
                {canApprovals && d.approvals.slice(0, 4).map((a) => (
                  <Row key={a.id} onClick={() => onNavigate('hitlWorkbench')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{tx(a.title)}</span>
                      <span className="block truncate text-xs text-muted-foreground">{a.status === 'escalated' ? t('Escalated') : t('AI output awaiting review')} · {ago(a.created_at)}</span>
                    </span>
                    <SeverityPill severity={a.severity} />
                  </Row>
                ))}
                {canDecisions && d.decisions.slice(0, 3).map((x) => (
                  <Row key={x.id} onClick={() => onNavigate('decisions')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{tx(x.title)}</span>
                      <span className="block truncate text-xs text-muted-foreground">{t('Decision pending')}{x.project_name ? ` · ${x.project_name}` : ''}</span>
                    </span>
                    <SeverityPill severity={x.priority} />
                  </Row>
                ))}
                {attention.map((task) => (
                  <Row key={task.id} onClick={() => onNavigate('projects')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{tx(task.title)}</span>
                      <span className="block truncate text-xs text-muted-foreground">{task.status === 'blocked' ? t('Blocked') : t('Overdue since {date}', { date: task.due_date ? shortDate(task.due_date) : '' })}</span>
                    </span>
                  </Row>
                ))}
              </ul>
            )}
          </Block>
        )}

        <Block icon={<Sparkles className="h-5 w-5" />} title={t('Projects')} action={t('Portfolio overview')} onAction={() => onNavigate('overview')}>
          {d.projects.length === 0 ? (
            <Empty>{t('No projects yet.')}</Empty>
          ) : (
            <ul>
              {d.projects.map((p) => (
                <Row key={p.id} onClick={() => onNavigate('overview')}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{p.name}</span>
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={p.progress ?? 0} aria-valuemin={0} aria-valuemax={100} aria-label={t('{name} progress', { name: p.name })}>
                      <span className="block h-full rounded-full bg-primary" style={{ width: `${p.progress ?? 0}%` }} />
                    </span>
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{p.progress ?? 0}%</span>
                  <span className={cn('rounded-full px-2 py-0.5 font-mono text-[11px] uppercase', p.health === 'on-track' ? 'bg-success/15 text-foreground' : p.health === 'delayed' ? 'bg-destructive/10 text-destructive' : 'bg-warning/15 text-foreground')}>
                    {t(p.health ?? 'n/a')}
                  </span>
                </Row>
              ))}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
};

export default TodayView;
